import { useQuery } from '@tanstack/react-query'
import * as pdfjsLib from 'pdfjs-dist'
import { type ReactNode } from 'react'

import { cn } from '@/lib/utils'

/** served from jsdelivr instead of via vite's `?url` import. vite's
 * resolution of the worker through pnpm-nested node_modules has been
 * flaky. pinning by package version keeps the worker and the main
 * library in sync regardless of how vite resolves the asset.
 */
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`

async function renderFirstPage(
  url: string,
  targetWidth: number
): Promise<string> {
  const pdf = await pdfjsLib.getDocument({ url }).promise
  const page = await pdf.getPage(1)
  const baseViewport = page.getViewport({ scale: 1 })
  const scale = targetWidth / baseViewport.width
  const viewport = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  const canvasContext = canvas.getContext('2d')
  if (!canvasContext) throw new Error('canvas 2d context unavailable')
  await page.render({ canvasContext, viewport }).promise
  return canvas.toDataURL('image/png')
}

type Props = {
  /** documentId keys the cache so a presigned-url rotation doesn't
   * invalidate the rendered thumbnail.
   */
  documentId: string
  url: string
  fileName: string
  targetWidth?: number
  className?: string
  fallback: ReactNode
}

export function PdfThumbnail({
  documentId,
  url,
  fileName,
  targetWidth = 360,
  className,
  fallback
}: Props) {
  const query = useQuery({
    queryKey: ['pdf-thumbnail', documentId, targetWidth],
    queryFn: () => renderFirstPage(url, targetWidth),
    staleTime: Infinity,
    enabled: Boolean(url),
    retry: 0
  })

  if (query.data) {
    return (
      <img
        src={query.data}
        alt={fileName}
        loading='lazy'
        className={cn('size-full object-cover object-top', className)}
      />
    )
  }
  return <>{fallback}</>
}
